/**
 * De teksten van de meetmoment-signalering, op één plek zodat de dashboardkaart,
 * de overzicht-kaart en de chips-rij hetzelfde zeggen.
 */

const DAYS_PER_MONTH = 30
const DAYS_PER_YEAR = 365

/**
 * Hoe lang iets al openstaat, in woorden.
 *
 * `'onbekend'` is een echte uitkomst en geen streepje: zonder ankerpunt weten
 * we het niet, en dat mag niet lijken op nul.
 *
 * @param {number|null|undefined} days
 * @returns {string}
 */
const formatWaitDuration = (days) => {
    if (typeof days !== 'number' || !Number.isFinite(days)) return 'onbekend'
    if (days <= 0) return 'vandaag'

    if (days >= DAYS_PER_YEAR) {
        const years = Math.floor(days / DAYS_PER_YEAR)
        return years === 1 ? '1 jaar' : `${years} jaar`
    }
    if (days >= DAYS_PER_MONTH) {
        const months = Math.floor(days / DAYS_PER_MONTH)
        return months === 1 ? '1 maand' : `${months} maanden`
    }
    return days === 1 ? '1 dag' : `${days} dagen`
}

/**
 * Over hoeveel dagen het meetmoment de deur uit moet, in woorden.
 *
 * Er komt nooit een minteken op het scherm: een datum die al gepasseerd is
 * leest als 'te laat', niet als '-3 dagen'.
 * AI-CONTEXT: See docs/pitfalls.md#ui-negative-day-count-reads-as-days-ago
 *
 * `'onbekend'` is een echte uitkomst en geen streepje — zonder eigen tussentijd
 * van het onderzoek weten we het niet, en dat mag niet lijken op 'vandaag'.
 *
 * @param {number|null|undefined} days - Uitkomst van `daysUntilDue`
 * @returns {string}
 */
const formatDueCountdown = (days) => {
    if (typeof days !== 'number' || !Number.isFinite(days)) return 'onbekend'
    if (days === 0) return 'vandaag'
    if (days > 0) return days === 1 ? 'over 1 dag' : `over ${days} dagen`

    const late = Math.abs(days)
    return late === 1 ? '1 dag te laat' : `${late} dagen te laat`
}

/**
 * Hoe lang er niets meer binnenkwam, in woorden.
 *
 * Voor een onderzoek zonder herleidbare invullers is dit wat er in de plaats
 * komt van een aftelling: zonder deelnemer is er geen startpunt, dus geen
 * termijn. Stilte is wél te meten.
 *
 * `null` is een eigen uitkomst: nog nooit een antwoord is iets anders dan nul
 * dagen stilte, en dat verschil moet op het scherm te zien zijn.
 *
 * @param {number|null|undefined} days
 * @returns {string}
 */
const formatSilence = (days) => {
    if (typeof days !== 'number' || !Number.isFinite(days)) return 'nog niets binnen'
    if (days <= 0) return 'vandaag nog'
    return days === 1 ? '1 dag niets' : `${days} dagen niets`
}

/** Nederlandse maandafkortingen. Met de hand, niet via `toLocaleDateString`:
 *  een node zonder volledige ICU geeft daar stil Engelse namen terug.
 *  AI-CONTEXT: See docs/pitfalls.md#ui-rrule-totext-bypasses-gettext */
const MONTHS_SHORT = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec']

/**
 * Wanneer het laatste antwoord op dit meetmoment binnenkwam.
 *
 * Het jaar komt er alleen bij zodra het een ander jaar is dan nu — anders leest
 * elke regel als een archiefstuk.
 *
 * @param {number|null|undefined} ms - Tijdstip in milliseconden
 * @param {number} [now] - Nu, in milliseconden
 * @returns {string} Lege tekst als er geen datum is
 */
const formatLastAnswer = (ms, now = Date.now()) => {
    if (typeof ms !== 'number' || !Number.isFinite(ms)) return ''

    const date = new Date(ms)
    const label = `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]}`
    return date.getFullYear() === new Date(now).getFullYear()
        ? `laatste ${label}`
        : `laatste ${label} ${date.getFullYear()}`
}

/** @type {Record<string, string>} */
const URGENCY_LABELS = {
    overdue: 'Te laat',
    due: 'Bijna zover',
    open: 'Staat open',
    upcoming: 'Binnenkort',
    waiting: 'Traject loopt nog',
    complete: 'Alles ingevuld',
    unknown: 'Niet te zeggen',
}

/**
 * @param {string} urgency
 * @returns {string}
 */
const urgencyLabel = (urgency) => URGENCY_LABELS[urgency] || URGENCY_LABELS.open

const DUE_DATE_OPTIONS = { year: 'numeric', month: 'long', day: 'numeric' }

/**
 * Wanneer het meetmoment van een huishouden aan de beurt is, in woorden.
 *
 * Alleen de laatste meting van een huishouden heeft zo'n regel: zes weken na
 * de afronding van het traject. Bij een rij zonder die regel komt er een lege
 * tekst uit, en géén datum uit iets anders afgeleid: een verzonnen datum is
 * erger dan geen datum.
 *
 * @param {{dueAtMs?: number|null, urgency?: string, reason?: string|null}|null|undefined} row
 * @returns {string} Lege tekst als er niets over de datum te zeggen valt
 */
const describeDue = (row) => {
    if (!row) return ''

    const dueAtMs = row.dueAtMs
    if (typeof dueAtMs === 'number' && Number.isFinite(dueAtMs)) {
        const date = new Date(dueAtMs).toLocaleDateString('nl-NL', DUE_DATE_OPTIONS)
        return row.urgency === 'overdue'
            ? `was aan de beurt op ${date}`
            : `aan de beurt op ${date}`
    }

    if (row.reason === 'traject-running') return 'zes weken na afronding van het traject'
    // Afgerond, maar de afronding is nooit vastgelegd: dat is iets anders dan
    // 'geen datum', en het scherm moet het verschil kunnen laten zien.
    if (row.reason === 'traject-completed') return 'traject afgerond, afrondingsdatum onbekend'
    return ''
}

/** @type {Record<string, string>} */
const MOMENT_STATUS_LABELS = {
    filled: 'ingevuld',
    missing: 'nog niet ingevuld',
    // Geen achterstand maar een gepasseerd station: deze deelnemer was al
    // onderweg toen het meetmoment werd aangemaakt.
    missed: 'niet meer mogelijk',
    unknown: 'niet te zeggen',
}

/**
 * Het teken vóór een meetmoment. Bewust naast de kleur, nooit in plaats
 * daarvan: wie geen kleur ziet, moet het verschil nog steeds kunnen lezen.
 *
 * @type {Record<string, string>}
 */
const MOMENT_STATUS_MARKS = {
    filled: '\u2713',
    missing: '\u25cb',
    // Geen achterstand maar een gepasseerd station — een streepje, geen kruis.
    missed: '\u2013',
    unknown: '?',
}

/**
 * Wat er over de vórige meting te zeggen valt — de vraag waar een begeleider
 * op vastloopt: heeft een tweede meting bij deze persoon eigenlijk wel zin?
 *
 * @param {{moments?: Array<object>, next?: object|null, hasGap?: boolean}} row
 * @returns {string}
 */
const describePrevious = (row) => {
    if (row?.hasGap) return 'Let op: een latere meting is al ingevuld'

    const moments = row?.moments || []
    const index = moments.findIndex((moment) => moment.ID === row?.next?.ID)
    if (index <= 0) return 'Eerste meting'

    const previous = moments[index - 1]
    const label = MOMENT_STATUS_LABELS[previous.status] || MOMENT_STATUS_LABELS.unknown
    return `${previous.Title || 'Vorige meting'}: ${label}`
}

/** @type {Record<string, string>} */
const START_SOURCE_LABELS = {
    statusHistory: 'geschat uit de statushistorie',
    created: 'geschat uit de aanmaakdatum',
}

/**
 * Waar de startdatum vandaan komt, als dat er voor de lezer toe doet.
 *
 * Een expliciet ingevulde datum krijgt geen toevoeging; een afgeleide wél.
 * Een schatting die eruitziet als een harde datum is erger dan geen datum.
 *
 * @param {string|null} source
 * @returns {string} Lege tekst als er niets te melden valt
 */
const startSourceLabel = (source) => START_SOURCE_LABELS[source] || ''

export {
    formatWaitDuration,
    formatDueCountdown,
    formatSilence,
    formatLastAnswer,
    urgencyLabel,
    describePrevious,
    describeDue,
    startSourceLabel,
    URGENCY_LABELS,
    MOMENT_STATUS_LABELS,
    MOMENT_STATUS_MARKS,
}
